import { Box, Heading, Link, Skeleton, Text } from '@razorpay/blade/components';
import React, { useMemo } from 'react';
import useSalaryComponentsList from 'components/RunPayroll/Popups/EditSalary/hooks/useSalaryComponentsList';
import { DeductionsComponentType, EarningsComponentType } from 'components/SalaryComponents/types';
import { useQuery } from 'react-query';
import api from 'api';
import { routePaths } from 'components/Routes/data';
import useListSalaryStructures from 'components/SalaryStructures/hooks/useListSalaryStructures';

export const SalaryComponentsEntrypoint = ({
  isM3S2Enabled,
  isM3S1Enabled,
}: {
  isM3S2Enabled: boolean;
  isM3S1Enabled: boolean;
}) => {
  const { data: earningsComponents, isFetchingSalaryComponents: isFetchingEarningsComponents } =
    useSalaryComponentsList<EarningsComponentType>({
      requestParams: {
        category: 'EARNINGS_CATEGORY',
        status: 'COMPONENT_STATUS_ACTIVE',
      },
    });
  const { data: deductionsComponents, isFetchingSalaryComponents: isFetchingDeductionsComponents } =
    useSalaryComponentsList<DeductionsComponentType>({
      requestParams: {
        category: 'DEDUCTIONS_CATEGORY',
        status: 'COMPONENT_STATUS_ACTIVE',
      },
    });

  const { data: salaryStructures, isFetching: isFetchingSalaryStructures } =
    useListSalaryStructures({
      enabled: isM3S1Enabled,
      filters: 'SALARY_STRUCTURE_STATUS_ACTIVE',
      queryKey: ['salary-structures'],
    });

  const componentsTotalHeading = useMemo(() => {
    return `${earningsComponents.length + deductionsComponents.length} Components ( ${
      earningsComponents.length
    } Earnings, ${deductionsComponents.length} Deductions )`;
  }, [earningsComponents, deductionsComponents]);

  const salaryStructuresTotalHeading = useMemo(() => {
    return `${salaryStructures?.length || 0} Structures ( ${
      salaryStructures?.filter((structure) => structure.status === 'SALARY_STRUCTURE_STATUS_ACTIVE')
        .length || 0
    } 
      Active, ${
        salaryStructures?.filter(
          (structure) => structure.status === 'SALARY_STRUCTURE_STATUS_INACTIVE',
        ).length || 0
      } 
      Disabled )`;
  }, [salaryStructures]);

  const { data: flexibleBenefitComponents, isFetching: isFetchingFlexibleBenefitComponents } =
    useQuery({
      queryFn: () =>
        api.flexibleBenefitComponents.queries.getFlexibleBenefitsList({
          offset: 0,
          limit: 100,
        }),
      queryKey: [api.flexibleBenefitComponents.queries.getFlexibleBenefitsList],
      select: (data) => data.payrollListFlexibleBenefitsComponents.data,
      enabled: isM3S2Enabled,
    });

  const isFetchingComponents =
    isFetchingEarningsComponents ||
    isFetchingDeductionsComponents ||
    isFetchingFlexibleBenefitComponents ||
    isFetchingSalaryStructures;

  return (
    <Box marginBottom={'48px'}>
      {isFetchingComponents ? (
        <Skeleton height={'160px'} width={'100%'} />
      ) : (
        <>
          <Box padding={'spacing.4'} paddingLeft={'spacing.0'}>
            <Heading weight="semibold" size="medium" color="surface.text.gray.normal">
              Component Library
            </Heading>
          </Box>
          <Box
            display={'flex'}
            flexDirection={'column'}
            backgroundColor={'surface.background.gray.intense'}
            paddingY={'spacing.3'}
            paddingX={'spacing.0'}
            width={'calc(100% + 16px)'}
            borderColor={'surface.border.gray.subtle'}
            borderWidth={'thin'}>
            <Box
              flexDirection={'row'}
              display={'flex'}
              borderBottomColor={'surface.border.gray.subtle'}
              borderBottomWidth={'thin'}>
              <Box width={'30%'} padding={'spacing.4'} textAlign={'right'}>
                <Text color="surface.text.gray.muted">Salary Component Library </Text>
              </Box>
              <Box
                width={'70%'}
                padding={'spacing.4'}
                display={'flex'}
                justifyContent={'space-between'}>
                <Text color="surface.text.gray.normal">{componentsTotalHeading}</Text>
                <Link
                  href={routePaths.salaryComponents.root}
                  testID="manage-salary-components-link">
                  Edit
                </Link>
              </Box>
            </Box>
            {isM3S1Enabled ? (
              <Box flexDirection={'row'} display={'flex'}>
                <Box width={'30%'} padding={'spacing.4'} textAlign={'right'}>
                  <Text color="surface.text.gray.muted">Salary Structures </Text>
                </Box>
                <Box
                  width={'70%'}
                  padding={'spacing.4'}
                  display={'flex'}
                  justifyContent={'space-between'}>
                  <Text color="surface.text.gray.normal">{salaryStructuresTotalHeading}</Text>
                  <Link href={routePaths.salaryStructures.root}>Edit</Link>
                </Box>
              </Box>
            ) : null}
            {isM3S2Enabled ? (
              <Box
                flexDirection={'row'}
                display={'flex'}
                borderTopColor={'surface.border.gray.subtle'}
                borderTopWidth={'thin'}>
                <Box width={'30%'} padding={'spacing.4'} textAlign={'right'}>
                  <Text color="surface.text.gray.muted">Flexible Benefits </Text>
                </Box>
                <Box
                  width={'70%'}
                  padding={'spacing.4'}
                  display={'flex'}
                  justifyContent={'space-between'}>
                  <Text color="surface.text.gray.normal">
                    {flexibleBenefitComponents?.length || 0} Components
                  </Text>
                  <Link href={routePaths.flexibleBenefits.listView}>Edit</Link>
                </Box>
              </Box>
            ) : null}
          </Box>
        </>
      )}
    </Box>
  );
};
